declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    google: typeof google;
    initGoogleMaps: () => void;
  }
}

let googleMapsLoaded = false;
let googleMapsLoading = false;
let googleMapsCallbacks: (() => void)[] = [];

export function loadGoogleMaps(): Promise<void> {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (googleMapsLoaded && window.google?.maps) {
      resolve();
      return;
    }

    // If already loading, add to callbacks
    if (googleMapsLoading) {
      googleMapsCallbacks.push(resolve);
      return;
    }

    googleMapsLoading = true;

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script exists but might not be loaded yet
      const checkLoaded = () => {
        if (window.google?.maps) {
          googleMapsLoaded = true;
          googleMapsLoading = false;
          resolve();
          googleMapsCallbacks.forEach((callback) => callback());
          googleMapsCallbacks = [];
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
      return;
    }

    // Create the script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;

    // Set up the callback
    window.initGoogleMaps = () => {
      googleMapsLoaded = true;
      googleMapsLoading = false;
      resolve();
      googleMapsCallbacks.forEach((callback) => callback());
      googleMapsCallbacks = [];
    };

    // Handle script load errors
    script.onerror = () => {
      googleMapsLoading = false;
      reject(new Error('Failed to load Google Maps API'));
    };

    // Add the script to the document
    document.head.appendChild(script);
  });
}

export function isGoogleMapsLoaded(): boolean {
  return googleMapsLoaded && !!window.google?.maps;
}
